// Shared presentational primitives for the FinOps v2 panels. Matches the app's
// existing card/typography language (Monitoring Overview cards) with tabular
// numerals and the reserved semantic palette. No data fetching here.

import React from 'react';
import { Card, CardContent } from '../ui/card';
import { ArrowDown, ArrowUp, Minus, Database } from 'lucide-react';
import { formatMoney, formatPct } from './finopsFormat';

const LABEL = 'text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary';

// Literal class strings per tint (Tailwind JIT can't see interpolated names).
const TINTS = {
  slate: 'bg-slate-50 dark:bg-slate-500/10 text-slate-600 dark:text-slate-400',
  blue: 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400',
  violet: 'bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400',
  emerald: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  amber: 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400',
  rose: 'bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400',
};

// A compact KPI tile: label, big tabular value, optional sub-line + accent.
export function StatTile({ label, value, sub, accent, icon: Icon, iconTint = 'slate' }) {
  const tint = TINTS[iconTint] || TINTS.slate;
  return (
    <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <p className={LABEL}>{label}</p>
          {Icon && (
            <div className={`p-2 rounded-md ${tint}`}>
              <Icon className="w-4 h-4" />
            </div>
          )}
        </div>
        <p className={`text-2xl font-semibold tabular-nums ${accent || 'text-slate-900 dark:text-white'}`}>{value}</p>
        {sub && <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

// ── Stat strip ──────────────────────────────────────────────────────────────
// KPI presentation as ONE shared surface with hairline-divided cells (the
// Stripe/Linear pattern) instead of a row of identical icon-badge cards — the
// numbers carry the hierarchy, not decoration. The gap-px + tinted backdrop
// trick draws perfect 1px dividers in both directions at any responsive wrap.
export function StatStrip({ children, cols = 4, className = '' }) {
  const grid = cols === 3 ? 'grid-cols-1 sm:grid-cols-3'
    : cols === 6 ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6'
    : 'grid-cols-2 lg:grid-cols-4';
  return (
    <div className={`rounded-xl border border-slate-200 dark:border-surface-border shadow-sm overflow-hidden ${className}`}>
      <div className={`grid ${grid} gap-px bg-slate-100 dark:bg-slate-800`}>
        {children}
      </div>
    </div>
  );
}

// One cell of a StatStrip: micro-eyebrow, large tabular value (optionally
// toned/with a delta badge beside it), quiet sub-line. No icons — restraint is
// the design.
export function StatCell({ label, value, sub, tone, delta }) {
  return (
    <div className="bg-white dark:bg-surface-dark px-5 py-4 min-w-0">
      <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5 truncate">
        {label}
      </p>
      <div className="flex items-baseline gap-2 min-w-0">
        <p className={`text-[22px] leading-7 font-semibold tabular-nums tracking-tight truncate ${tone || 'text-slate-900 dark:text-white'}`}>
          {value}
        </p>
        {delta}
      </div>
      {sub && <p className="text-xs text-slate-500 dark:text-text-secondary mt-1 truncate">{sub}</p>}
    </div>
  );
}

// Δ vs previous period. Positive Δ (cost up) is bad → red; down → green.
export function DeltaBadge({ deltaUsd, deltaPct }) {
  if (deltaUsd == null && deltaPct == null) {
    return <span className="text-slate-400 dark:text-slate-500 text-xs tabular-nums">—</span>;
  }
  const up = (deltaUsd ?? deltaPct ?? 0) > 0;
  const flat = (deltaUsd ?? 0) === 0 && (deltaPct ?? 0) === 0;
  const Icon = flat ? Minus : up ? ArrowUp : ArrowDown;
  const tone = flat ? 'text-slate-400' : up ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400';
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium tabular-nums ${tone}`}>
      <Icon className="w-3 h-3" />
      {deltaPct != null ? formatPct(Math.abs(deltaPct)) : formatMoney(Math.abs(deltaUsd), { compact: true })}
    </span>
  );
}

// Thin inline utilization bar (used vs allocated in tables).
export function UtilBar({ pct, tint = 'bg-blue-500' }) {
  const v = Math.max(0, Math.min(100, pct ?? 0));
  const warn = v >= 90;
  return (
    <div className="flex items-center gap-2 min-w-[90px]">
      <div className="flex-1 h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
        <div className={`h-full rounded-full ${warn ? 'bg-amber-500' : tint}`} style={{ width: `${v}%` }} />
      </div>
      <span className="text-xs tabular-nums text-slate-500 dark:text-text-secondary w-9 text-right">
        {pct == null ? '—' : `${Math.round(v)}%`}
      </span>
    </div>
  );
}

// Skeleton block sized to the final layout (no spinner jumps).
export function Skeleton({ className = 'h-40' }) {
  return <div className={`skeleton rounded-xl ${className}`} />;
}

// Shown when ClickHouse is disabled (503) — hides the panel gracefully.
export function ChDisabledCard({ title = 'Advanced analytics unavailable' }) {
  return (
    <Card className="border border-dashed border-slate-300 dark:border-surface-border bg-slate-50/50 dark:bg-surface-dark">
      <CardContent className="py-12">
        <div className="text-center">
          <Database className="w-10 h-10 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <h3 className="text-sm font-semibold text-slate-700 dark:text-white mb-1">{title}</h3>
          <p className="text-xs text-slate-500 dark:text-text-secondary max-w-sm mx-auto">
            FinOps analytics require the ClickHouse metrics backend. The standard charts remain available.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// A designed empty state for "no data in this window".
export function EmptyCard({ message = 'No data for this window' }) {
  return (
    <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark">
      <CardContent className="py-12 text-center">
        <p className="text-sm text-slate-500 dark:text-text-secondary">{message}</p>
      </CardContent>
    </Card>
  );
}

// Section header with optional right-side actions (e.g. group-by toggle, export).
export function SectionHeader({ title, hint, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
      <div>
        <h3 className="text-[15px] font-semibold tracking-tight text-slate-900 dark:text-white">{title}</h3>
        {hint && <p className="text-xs text-slate-500 dark:text-text-secondary mt-0.5">{hint}</p>}
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}

// Designed chart legend: compact chips in the panel header instead of recharts'
// default bottom-center legend. Hidden for a single series (the title names it);
// series beyond `max` fold into a "+N more" note rather than wrapping forever.
// `dash: true` renders a dashed-line swatch (for reference/projection lines).
export function ChartLegend({ items, max = 8, className = '' }) {
  if (!items || items.length < 2) return null;
  const shown = items.slice(0, max);
  const extra = items.length - shown.length;
  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 ${className}`}>
      {shown.map((it) => (
        <span
          key={it.label}
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 dark:text-text-secondary"
        >
          {it.dash ? (
            <span className="w-3.5 border-t-2 border-dashed" style={{ borderColor: it.color }} />
          ) : (
            <span className="w-2 h-2 rounded-[3px] shrink-0" style={{ background: it.color }} />
          )}
          <span className="truncate max-w-[120px]">{it.label}</span>
        </span>
      ))}
      {extra > 0 && (
        <span className="text-xs text-slate-400 dark:text-slate-500">+{extra} more</span>
      )}
    </div>
  );
}

// A small segmented control (used for group-by, rate/cumulative, sort).
export function Segmented({ options, value, onChange, size = 'sm' }) {
  const pad = size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm';
  return (
    <div className="inline-flex rounded-md bg-slate-100 dark:bg-slate-800 p-0.5 border border-slate-200 dark:border-surface-border">
      {options.map((o) => {
        const val = typeof o === 'string' ? o : o.value;
        const label = typeof o === 'string' ? o : o.label;
        const active = val === value;
        return (
          <button
            key={val}
            onClick={() => onChange(val)}
            className={`${pad} rounded font-medium transition-colors ${
              active
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 dark:text-text-secondary hover:text-slate-700 dark:hover:text-white'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
